import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import DateControl from 'components/Form/DateControl';
import NotApplicable from 'components/Form/NotApplicable';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Name from 'components/Form/Name';
import Show from 'components/Form/Show';
import Branch from 'components/Form/Branch';
import Textarea from 'components/Form/Textarea';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { FinancialBankruptcyItemModel } from 'forms/sections/financial/bankruptcy/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonRequired,
  CommonScrollIntoView,
  CommonAddressBooks,
  OnError,
  OnUpdate,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { Dispatch } from 'redux';
import { includesAny } from 'helpers/string/string';
import { isPVQForm } from 'helpers/form';
import { BANKRUPTCY_CHAPTER_VALUES_REQUIRING_TRUSTEE, bankruptcyChapterOptions } from 'constants/enums/financialOptions';
import PVQName from 'components/Form/PVQName';
import PVQLocation from 'components/Form/PVQLocation';
import Layout from 'components/Form/PVQLocation/Layouts';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import { LocationModel, NameModel } from 'forms/shared/types';

type UpdateField<Model extends {}> = <Key extends keyof Model>(
  field: Key,
  values: Model[Key]
) => void

interface Props extends Partial<FinancialBankruptcyItemModel>, ValidationElementProps {
  onUpdate?: OnUpdate<Props>
  onError?: OnError
  required?: CommonRequired
  formType: string
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks

  requireFinancialBankruptcyTrusteeInformation?: boolean
  disabled?: boolean
  dispatch?: Dispatch
}

export default class Bankruptcy extends ValidationElement<Props> {
  handleNameError = (value: object | string, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError?.(value, arr2);
  };

  update = (queue: Partial<FinancialBankruptcyItemModel>) => {
    this.props.onUpdate?.({
      CourtNumberIdk: this.props.CourtNumberIdk,
      PetitionType: this.props.PetitionType,
      CourtNumber: this.props.CourtNumber,
      DateFiled: this.props.DateFiled,
      DateDischargedNotApplicable: this.props.DateDischargedNotApplicable,
      DateDischarged: this.props.DateDischarged,
      TotalAmount: this.props.TotalAmount,
      TotalAmountEstimated: this.props.TotalAmountEstimated,
      NameDebt: this.props.NameDebt,
      CourtInvolved: this.props.CourtInvolved,
      CourtAddress: this.props.CourtAddress,
      Trustee: this.props.Trustee,
      PvqTrustee: this.props.PvqTrustee,
      TrusteeAddress: this.props.TrusteeAddress,
      HasDischargeExplanation: this.props.HasDischargeExplanation,
      DischargeExplanation: this.props.DischargeExplanation,
      TrusteeNotApplicable: this.props.TrusteeNotApplicable,
      TrusteeAddressNotApplicable: this.props.TrusteeAddressNotApplicable,
      WasFiledJointly: this.props.WasFiledJointly,
      PvqTrusteeNameNotApplicable: this.props.PvqTrusteeNameNotApplicable,
      JointFileName: this.props.JointFileName,
      ...queue,
    });
  };

  updateCourtNumberIdk = (values: FinancialBankruptcyItemModel['CourtNumberIdk']) => {
    this.update({
      CourtNumberIdk: values,
    });
  };

  updateTotalAmount = (values: FinancialBankruptcyItemModel['TotalAmount']) => {
    this.update({
      TotalAmount: values,
    });
  };

  updateTotalAmountEstimated = (values: FinancialBankruptcyItemModel['TotalAmountEstimated']) => {
    this.update({
      TotalAmountEstimated: values,
    });
  };

  updateWasFiledJointly = (values: FinancialBankruptcyItemModel['WasFiledJointly']) => {
    this.update({
      WasFiledJointly: values,
    });
  };

  updateCourtAddress = (values: FinancialBankruptcyItemModel['CourtAddress']) => {
    this.update({
      CourtAddress: values,
    });
  };

  updateCourtInvolved = (values: FinancialBankruptcyItemModel['CourtInvolved']) => {
    this.update({
      CourtInvolved: values,
    });
  };

  updateJointFileName = (values: FinancialBankruptcyItemModel['JointFileName']) => {
    this.update({
      JointFileName: values,
    });
  };

  updateCourtNumber = (values: FinancialBankruptcyItemModel['CourtNumber']) => {
    this.update({
      CourtNumber: values,
    });
  };

  updateTrustee = (values: FinancialBankruptcyItemModel['Trustee']) => {
    this.update({
      Trustee: values,
    });
  };

  updatePvqTrustee = (values: FinancialBankruptcyItemModel['PvqTrustee']) => {
    this.update({
      PvqTrustee: values,
    });
  };

  updateTrusteeAddress = (values: FinancialBankruptcyItemModel['TrusteeAddress']) => {
    this.update({
      TrusteeAddress: values,
    });
  };

  updateHasDischargeExplanation = (values: FinancialBankruptcyItemModel['HasDischargeExplanation']) => {
    this.update({
      HasDischargeExplanation: values,
      DischargeExplanation: values?.value === 'No' ? this.props.DischargeExplanation : {} as FinancialBankruptcyItemModel['DischargeExplanation'],
      DateDischarged: values?.value === 'Yes' ? this.props.DateDischarged : {} as FinancialBankruptcyItemModel['DateDischarged'],
    });
  };

  updateDischargeExplanation = (values: FinancialBankruptcyItemModel['DischargeExplanation']) => {
    this.update({
      DischargeExplanation: values,
    });
  };

  updateDateDischarged = (values: FinancialBankruptcyItemModel['DateDischarged']) => {
    this.update({
      DateDischarged: values,
    });
  };

  updateDateDischargedNotApplicable = (
    values: FinancialBankruptcyItemModel['DateDischargedNotApplicable']
  ) => {
    this.update({
      DateDischarged: values.applicable ? this.props.DateDischarged : {} as FinancialBankruptcyItemModel['DateDischarged'],
      DateDischargedNotApplicable: values,
    });
  };

  updateField: UpdateField<FinancialBankruptcyItemModel> = (field, values) => {
    this.update({
      [field]: values,
    });
  };

  updatePetitionType = (value: FinancialBankruptcyItemModel['PetitionType']) => {
    this.updateField('PetitionType', value);
  };

  updateTrusteeNotApplicable = (value: FinancialBankruptcyItemModel['TrusteeNotApplicable']) => {
    this.update({
      Trustee: value.applicable ? this.props.Trustee : {} as FinancialBankruptcyItemModel['Trustee'],
      TrusteeNotApplicable: value,
    });
  };

  updateNameDebt = (values: FinancialBankruptcyItemModel['NameDebt']) => {
    this.update({
      NameDebt: values,
    });
  };

  updateTrusteeAddressNotApplicable = (value: FinancialBankruptcyItemModel['TrusteeAddressNotApplicable']) => {
    this.update({
      TrusteeAddress: value.applicable ? this.props.TrusteeAddress : {} as FinancialBankruptcyItemModel['TrusteeAddress'],
      TrusteeAddressNotApplicable: value,
    });
  };

  updateDateFiled = (value: FinancialBankruptcyItemModel['DateFiled']) => {
    this.update({
      DateFiled: value,
    });
  };

  updatePvqTrusteeNameNotApplicable = (value: FinancialBankruptcyItemModel['PvqTrusteeNameNotApplicable']) => {
    this.update({
      PvqTrusteeNameNotApplicable: value,
      PvqTrustee: value?.applicable ? this.props.PvqTrustee : {},
    });
  };

  render() {
    const { requireFinancialBankruptcyTrusteeInformation, $index } = this.props;
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    const showFinancialBankruptcyTrusteeInformation: boolean = isPVQ
      && BANKRUPTCY_CHAPTER_VALUES_REQUIRING_TRUSTEE.includes(this.props.PetitionType?.value || '');

    return isPVQ ? (
      <div className="bankruptcy">
        <Field
          title={i18n.t('financial.pvq.bankruptcy.branch.ifYes.chapterRadio')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="buttons"
          attribute={`${attribute}.PetitionType`}
        >
          <RadioGroup
            className="petition-chapters option-list option-list-vertical"
            selectedValue={(this.props.PetitionType || {}).value}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.PetitionType`}
          >
            {bankruptcyChapterOptions.map((chapter) => (
              <Radio
                name="petition_type"
                key={chapter}
                label={chapter}
                value={chapter}
                onUpdate={this.updatePetitionType}
                onError={this.props.onError}
              />
            ))}
          </RadioGroup>
        </Field>

        <Branch
          value={this.props.WasFiledJointly?.value}
          name="wasfiledjointly"
          label={i18n.t('financial.pvq.bankruptcy.branch.ifYes.branchIdk.title')}
          labelSize="h4"
          className="joint-file-branch"
          onUpdate={this.updateWasFiledJointly}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.handleError}
          attribute={`${attribute}.WasFiledJointly`}
        />
        {this.props.WasFiledJointly?.value === 'Yes' && (
          <Field
            title={i18n.t('financial.pvq.bankruptcy.branch.ifYes.nameText')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.JointFileName`}
          >

            <PVQName
              attribute={`${attribute}.JointFileName`}
              aria-label="Provide full name"
              name="jointfilename"
              {...this.props.JointFileName}
              onUpdate={this.updateJointFileName}
              onError={this.props.onError} // eslint-disable-line @typescript-eslint/unbound-method
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              hideMiddleName
              showLastFirstMiddleNameOrder
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.bankruptcy.branch.ifYes.accountNumTextIdk')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtNumber`}
        >
          <NotApplicable
            applicable={this.props.CourtNumberIdk?.applicable}
            label={i18n.t('financial.pvq.shared.idk')}
            onUpdate={this.updateCourtNumberIdk}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtNumber`}
          >
            <Text
              name="CourtNumber"
              onUpdate={this.updateCourtNumber}
              onError={this.props.onError}
              value={this.props.CourtNumber?.value}
              className="courtnumber"
              required={this.props.required}
              attribute={`${attribute}.CourtNumber`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('financial.pvq.bankruptcy.branch.ifYes.courtNameText')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtInvolved`}
        >
          <Text
            name="CourtInvolved"
            value={this.props.CourtInvolved?.value}
            className="courtinvolved"
            onUpdate={this.updateCourtInvolved}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtInvolved`}
          />
        </Field>

        <Field
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="address"
          attribute={`${attribute}.CourtAddress`}
          title={i18n.t('financial.pvq.bankruptcy.branch.ifYes.courtLocation')}
        >
          <PVQLocation
            {...this.props.CourtAddress}
            name="CourtAddress"
            layout={Layout.US_STREET_CITY_COUNTY_STATE_ZIP}
            geocode
            dispatch={this.props.dispatch}
            addressBooks={this.props.addressBooks}
            addressBook="Court"
            onUpdate={this.updateCourtAddress}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtAddress`}
            countyLabel={i18n.t('address.pvq.us.county.financialLabel')}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.bankruptcy.branch.ifYes.whenFiled')}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="labels"
          attribute={`${attribute}.DateFiled`}
        >
          <DateControl
            {...this.props.DateFiled}
            name="DateFiled"
            onUpdate={this.updateDateFiled}
            minDateEqualTo
            onError={this.props.onError}
            className="datefiled"
            required={this.props.required}
            hideDay
            showEstimated
            attribute={`${attribute}.DateFiled`}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.bankruptcy.branch.ifYes.amountCurrency')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.TotalAmount`}
        >
          <Currency
            maxLength="10"
            name="TotalAmount"
            onUpdate={this.updateTotalAmount}
            onError={this.props.onError}
            value={this.props.TotalAmount?.value}
            className="amount"
            required={this.props.required}
            attribute={`${attribute}.TotalAmount`}
          />
          <div className="flags">
            <Checkbox
              name="TotalAmountEstimated"
              onUpdate={this.updateTotalAmountEstimated}
              onError={this.props.onError}
              value={this.props.TotalAmountEstimated?.value}
              checked={this.props.TotalAmountEstimated?.checked}
              label={i18n.t('financial.pvq.shared.estimated')}
              toggle="false"
              attribute={`${attribute}.TotalAmountEstimated`}
            />
          </div>
        </Field>

        <Field
          title={i18n.t('financial.pvq.bankruptcy.branch.ifYes.usedName')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQName
            {...this.props.NameDebt}
            name="NameDebt"
            className="namedebt"
            onUpdate={this.updateNameDebt}
            onError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.NameDebt`}
            showLastFirstMiddleNameOrder
          />
        </Field>

        {showFinancialBankruptcyTrusteeInformation && (
          <>
            <Field
              title={i18n.t('financial.pvq.bankruptcy.branch.ifYes.adminName')}
              titleSize="h4"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.PvqTrustee`}
            >
              <NotApplicable
                {...this.props.PvqTrusteeNameNotApplicable}
                attribute={`${attribute}.PvqTrusteeNameNotApplicable`}
                className="pvq-trustee-not-applicable"
                includeNAConfirmation
                label={i18n.t('military.pvq.disciplinary.label.idk')}
                name="PvqTrusteeNameNotApplicable"
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                onError={this.props.onError}
                onUpdate={this.updatePvqTrusteeNameNotApplicable}
                or={i18n.t('military.pvq.disciplinary.para.or')}
                required={this.props.required}
              >
                <PVQName
                  {...this.props.PvqTrustee}
                  attribute={`${attribute}.PvqTrustee`}
                  name="trusteename"
                  onUpdate={this.updatePvqTrustee}
                  onError={this.props.onError}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                  showLastFirstMiddleNameOrder
                />
              </NotApplicable>
            </Field>

            <PVQLocationUnknown
              {...this.props.TrusteeAddress}
              addressUnknown={this.props.TrusteeAddressNotApplicable}
              addressUnknownLabel={i18n.t('address.pvq.idkCapitalized')}
              attribute={`${attribute}.TrusteeAddress`}
              country="United States"
              disableToggle
              label={i18n.t('financial.pvq.bankruptcy.branch.ifYes.adminLocation')}
              layout={Layout.US_ADDRESS}
              name="TrusteeAddress"
              onError={this.props.onError}
              onUpdate={this.updateTrusteeAddress}
              onUpdateAddressUnknown={this.updateTrusteeAddressNotApplicable}
              required={this.props.required}
              showAddressUnknown
            />
          </>
        )}

        <Branch
          value={this.props.HasDischargeExplanation?.value}
          name="current"
          label={i18n.t('financial.pvq.bankruptcy.branch.ifYes.branch.title')}
          labelSize="h4"
          className="current-branch"
          onUpdate={this.updateHasDischargeExplanation}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.handleError}
          attribute={`${attribute}.HasDischargeExplanation`}
        />

        {this.props.HasDischargeExplanation?.value === 'Yes' && (
          <Field
            title={i18n.t('financial.pvq.bankruptcy.branch.ifYes.branch.ifYes.whenDateEstimated')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Year`}
          >
            <DateControl
              month={this.props.DateDischarged?.month}
              year={this.props.DateDischarged?.year}
              estimated={this.props.DateDischarged?.estimated}
              attribute={`${attribute}.DateDischarged`}
              onUpdate={this.updateDateDischarged}
              onError={this.props.onError}
              required={this.props.required}
              showEstimated
              hideDay
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>
        )}
        {this.props.HasDischargeExplanation?.value === 'No' && (
          <Field
            title={i18n.t('financial.pvq.bankruptcy.branch.ifYes.branch.ifNo.explainTextArea')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.DischargeExplanation`}
          >
            <Textarea
              value={this.props.DischargeExplanation?.value}
              attribute={`${attribute}.DischargeExplanation`}
              onUpdate={this.updateDischargeExplanation}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              showEstimated={false}
            />
          </Field>
        )}
      </div>
    ) : (
      <div className="bankruptcy">
        <Field
          title={i18n.t('financial.bankruptcy.heading.petitionType')}
          titleSize="h4"
          help="financial.bankruptcy.petitionType.help"
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="buttons"
          attribute={`${attribute}.PetitionType`}
        >
          <RadioGroup
            className="petition-chapters option-list option-list-vertical"
            selectedValue={(this.props.PetitionType || {}).value}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.PetitionType`}
          >
            <Radio
              name="petition_type"
              label={i18n.t('financial.bankruptcy.petitionType.label.chapter7')}
              value="Chapter7"
              disabled={this.props.disabled}
              onUpdate={this.updatePetitionType}
              onError={this.props.onError}
            />
            <Radio
              name="petition_type"
              label={i18n.t('financial.bankruptcy.petitionType.label.chapter11')}
              value="Chapter11"
              disabled={this.props.disabled}
              onUpdate={this.updatePetitionType}
              onError={this.props.onError}
            />
            <Radio
              name="petition_type"
              label={i18n.t('financial.bankruptcy.petitionType.label.chapter12')}
              value="Chapter12"
              disabled={this.props.disabled}
              onUpdate={this.updatePetitionType}
              onError={this.props.onError}
            />
            <Radio
              name="petition_type"
              label={i18n.t('financial.bankruptcy.petitionType.label.chapter13')}
              value="Chapter13"
              disabled={this.props.disabled}
              onUpdate={this.updatePetitionType}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>

        <Field
          title={i18n.t('financial.bankruptcy.heading.courtNumber')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtNumber`}
        >
          <Text
            name="CourtNumber"
            onUpdate={(value: FinancialBankruptcyItemModel['CourtNumber']) => {
              this.updateField('CourtNumber', value);
            }}
            onError={this.props.onError}
            {...this.props.CourtNumber}
            className="courtnumber"
            title={i18n.t('financial.bankruptcy.courtNumber.title')}
            required={this.props.required}
            attribute={`${attribute}.CourtNumber`}
          />
        </Field>

        <Field
          title={i18n.t('financial.bankruptcy.heading.dateFiled')}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="labels"
          attribute={`${attribute}.DateFiled`}
        >
          <DateControl
            name="DateFiled"
            onUpdate={(value: FinancialBankruptcyItemModel['DateFiled']) => {
              this.updateField('DateFiled', value);
            }}
            minDateEqualTo
            onError={this.props.onError}
            {...this.props.DateFiled}
            className="datefiled"
            required={this.props.required}
            hideDay
            attribute={`${attribute}.DateFiled`}
          />
        </Field>

        <Field
          title={i18n.t('financial.bankruptcy.heading.dateDischarged')}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="label"
          attribute={`${attribute}.DateDischarged`}
        >
          <NotApplicable
            name="DateDischargedNotApplicable"
            {...this.props.DateDischargedNotApplicable}
            onError={this.props.onError}
            onUpdate={this.updateDateDischargedNotApplicable}
            attribute={`${attribute}.DateDischargedNotApplicable`}
            includeNAConfirmation={Boolean(this.props.DateDischarged?.month || this.props.DateDischarged?.year)}
          >
            <DateControl
              name="DateDischarged"
              className="datedischarged"
              onUpdate={(value: FinancialBankruptcyItemModel['DateDischarged']) => {
                this.updateField('DateDischarged', value);
              }}
              prefix="bankruptcy"
              onError={this.props.onError}
              minDate={this.props.DateFiled}
              minDateEqualTo
              {...this.props.DateDischarged}
              required={this.props.required}
              hideDay
              attribute={`${attribute}.DateDischarged`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('financial.bankruptcy.heading.totalAmount')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.TotalAmount`}
        >
          <Currency
            name="TotalAmount"
            onUpdate={(value: FinancialBankruptcyItemModel['TotalAmount']) => {
              this.updateField('TotalAmount', value);
            }}
            onError={this.props.onError}
            {...this.props.TotalAmount}
            className="amount"
            min="1"
            required={this.props.required}
            attribute={`${attribute}.TotalAmount`}
          />
          <div className="flags">
            <Checkbox
              name="TotalAmountEstimated"
              onUpdate={(value: FinancialBankruptcyItemModel['TotalAmountEstimated']) => {
                this.updateField('TotalAmountEstimated', value);
              }}
              onError={this.props.onError}
              {...this.props.TotalAmountEstimated}
              label={i18n.t('financial.bankruptcy.totalAmount.estimated')}
              toggle="false"
              attribute={`${attribute}.TotalAmountEstimated`}
            />
          </div>
        </Field>

        <Field
          title={i18n.t('financial.bankruptcy.heading.nameDebt')}
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
          optional
        >
          <Name
            name="NameDebt"
            aria-label="Provide the name the debt is recorded under"
            className="namedebt"
            {...this.props.NameDebt}
            onUpdate={(value: FinancialBankruptcyItemModel['NameDebt']) => {
              this.updateField('NameDebt', value);
            }}
            onError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.NameDebt`}
          />
        </Field>

        <Field
          title={i18n.t('financial.bankruptcy.heading.courtInvolved')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtInvolved`}
        >
          <Text
            name="CourtInvolved"
            {...this.props.CourtInvolved}
            className="courtinvolved"
            onUpdate={(value: FinancialBankruptcyItemModel['CourtInvolved']) => {
              this.updateField('CourtInvolved', value);
            }}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtInvolved`}
          />
        </Field>

        <Field
          title={i18n.t('financial.bankruptcy.heading.courtAddress')}
          optional
          help="financial.bankruptcy.courtAddress.help"
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="address"
          attribute={`${attribute}.CourtAddress`}
        >
          <Location
            name="CourtAddress"
            label={i18n.t('financial.bankruptcy.courtAddress.label')}
            {...this.props.CourtAddress}
            layout={Location.ADDRESS}
            geocode
            dispatch={this.props.dispatch}
            addressBooks={this.props.addressBooks}
            addressBook="Court"
            onUpdate={(value: FinancialBankruptcyItemModel['CourtAddress']) => {
              this.updateField('CourtAddress', value);
            }}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtAddress`}
          />
        </Field>

        {(requireFinancialBankruptcyTrusteeInformation
          ? includesAny(this.props.PetitionType?.value ?? '', ['Chapter12', 'Chapter13'])
          : this.props.PetitionType?.value === 'Chapter13') && (
            <div className="chapter13">
              <Field
                title={i18n.t('financial.bankruptcy.trustee.title')}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.Trustee`}
                exactAttribute
              >
                <NotApplicable
                  name="TrusteeNotApplicable"
                  className="na-accountnumber"
                  label={i18n.t('financial.bankruptcy.label.idk')}
                  or={i18n.t('financial.bankruptcy.para.or')}
                  {...this.props.TrusteeNotApplicable}
                  onUpdate={this.updateTrusteeNotApplicable}
                  required={this.props.required}
                  onError={this.props.onError}
                  attribute={`${attribute}.TrusteeNotApplicable`}
                  includeNAConfirmation={Boolean(this.props.Trustee?.value)}
                >
                  <Text
                    name="chapter13Trustee"
                    className="trustee"
                    {...this.props.Trustee}
                    onError={this.props.onError}
                    onUpdate={this.updateTrustee}
                    required={this.props.required}
                    attribute={`${attribute}.Trustee`}
                  />
                </NotApplicable>
              </Field>

              <Field
                title={i18n.t('financial.bankruptcy.trustee.address.title')}
                optional
                help="financial.bankruptcy.trustee.address.help"
                scrollIntoView={this.props.scrollIntoView}
                adjustFor="address"
                attribute={`${attribute}.TrusteeAddress`}
              >
                <NotApplicable
                  name="TrusteeAddressNotApplicable"
                  className="na-trusteeaddress"
                  label={i18n.t('financial.bankruptcy.label.idk')}
                  or={i18n.t('financial.bankruptcy.para.or')}
                  {...this.props.TrusteeAddressNotApplicable}
                  onUpdate={this.updateTrusteeAddressNotApplicable}
                  required={this.props.required}
                  onError={this.props.onError}
                  attribute={`${attribute}.TrusteeAddressNotApplicable`}
                  includeNAConfirmation={Boolean(this.props.TrusteeAddress
                    && (this.props.TrusteeAddress.city
                      || this.props.TrusteeAddress.zipcode
                      || this.props.TrusteeAddress.street
                      || this.props.TrusteeAddress.state
                      || (
                        this.props.TrusteeAddress.country
                        && this.props.TrusteeAddress.country.value !== 'United States'
                        && this.props.TrusteeAddress.country.value !== 'INTERNATIONAL'
                      )
                    ))}
                >
                  <Location
                    name="trusteeAddress"
                    className="trustee-address"
                    {...this.props.TrusteeAddress}
                    label={i18n.t('financial.bankruptcy.trustee.address.label')}
                    layout={Location.ADDRESS}
                    geocode
                    dispatch={this.props.dispatch}
                    addressBooks={this.props.addressBooks}
                    addressBook="Court"
                    onError={this.props.onError}
                    onUpdate={(value: FinancialBankruptcyItemModel['TrusteeAddress']) => {
                      this.updateField('TrusteeAddress', value);
                    }}
                    required={this.props.required}
                    attribute={`${attribute}.TrusteeAddress`}
                  />
                </NotApplicable>
              </Field>
            </div>
          )}

        <Branch
          name="discharge_explanation"
          label={i18n.t('financial.bankruptcy.heading.dischargeExplanation')}
          labelSize="h4"
          className="has-discharge-explanation no-margin-bottom"
          {...this.props.HasDischargeExplanation}
          onUpdate={(value: FinancialBankruptcyItemModel['HasDischargeExplanation']) => {
            this.updateField('HasDischargeExplanation', value);
          }}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.HasDischargeExplanation`}
        />

        <Show when={(this.props.HasDischargeExplanation || {}).value}>
          <Field
            title={i18n.t('financial.bankruptcy.label.dischargeExplanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.DischargeExplanation`}
          >
            <Textarea
              name="DischargeExplanation"
              {...this.props.DischargeExplanation}
              className="discharge-explanation"
              onUpdate={(value: FinancialBankruptcyItemModel['DischargeExplanation']) => {
                this.updateField('DischargeExplanation', value);
              }}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.DischargeExplanation`}
            />
          </Field>
        </Show>
      </div>
    );
  }
}

Bankruptcy.defaultProps = {
  DateDischargedNotApplicable: { applicable: true },
  addressBooks: {},
  dispatch: () => { },
  onUpdate: () => { },
  onError: (value, arr: ErrorCode[]) => arr,
};
